import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { Plus, Users, MoreHorizontal, Pencil, Trash2 } from 'lucide-react';
import { Header, PageContainer } from '@/components/layout';
import {
  Button,
  Card,
  CardBody,
  StatusBadge,
  Badge,
  Avatar,
  EmptyState,
  Input,
  Select,
  Dropdown,
  DropdownItem,
  ConfirmDialog,
} from '@/components/ui';
import { EmployeeFormModal } from '@/components/forms';
import { useEmployees, useDeleteEmployee } from '@/hooks/useEmployees';
import { useAuthStore } from '@/stores/auth';
import { ApiError } from '@/services/api';
import { EMPLOYEE_ROLE_LABELS } from '@aelita/shared';
import type { Employee } from '@aelita/shared';

// Matches the code the API returns from DELETE /employees/:id when an
// admin tries to deactivate their own account (apps/api/src/routes/employees.ts).
const CANNOT_DELETE_SELF = 'CANNOT_DELETE_SELF';

export const Route = createFileRoute('/employees')({
  component: EmployeesPage,
});

export function EmployeesPage() {
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState<Employee | null>(null);
  const [deletingEmployee, setDeletingEmployee] = useState<Employee | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  // Default to hiding inactive employees so a deactivated person actually
  // disappears from the list (that's the whole point of #103/#105 - a
  // "Delete" that leaves the row sitting right there reads as broken).
  // "active,on_leave" (not "active") is deliberate: someone on leave is
  // still a current employee and must stay visible by default. Inactive
  // employees are still reachable via the status filter below (needed to
  // reactivate them from Edit).
  const DEFAULT_STATUS_FILTER = 'active,on_leave';
  const [filters, setFilters] = useState<{ status?: string; role?: string; search?: string }>({
    status: DEFAULT_STATUS_FILTER,
  });
  const { data, isLoading } = useEmployees(filters);
  const employees = data?.data ?? [];
  const { user } = useAuthStore();
  const isAdmin = user?.role === 'admin';
  const deleteEmployee = useDeleteEmployee();

  const openDeactivateDialog = (employee: Employee) => {
    setDeleteError(null);
    setDeletingEmployee(employee);
  };

  const closeDeactivateDialog = () => {
    setDeletingEmployee(null);
    setDeleteError(null);
  };

  const handleDelete = async () => {
    if (!deletingEmployee) return;
    try {
      await deleteEmployee.mutateAsync(deletingEmployee.id);
      setDeletingEmployee(null);
      setDeleteError(null);
    } catch (error) {
      // Never fail silently on a destructive action (incident #103: a
      // swallowed error here looked like clicking "Delete" did nothing).
      if (error instanceof ApiError && error.code === CANNOT_DELETE_SELF) {
        setDeleteError(
          "You can't deactivate your own account. Sign in as a different admin to deactivate this one.",
        );
      } else if (error instanceof ApiError) {
        setDeleteError(error.message);
      } else {
        setDeleteError('Something went wrong deactivating this employee. Please try again.');
      }
    }
  };

  return (
    <>
      <Header
        title="Employees"
        subtitle="Manage your team members"
        actions={
          isAdmin && (
            <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setIsCreateOpen(true)}>
              Add Employee
            </Button>
          )
        }
      />
      <PageContainer>
        {/* Filters */}
        <Card className="mb-6">
          <CardBody className="flex items-center gap-4">
            <Input
              placeholder="Search employees..."
              className="max-w-xs"
              value={filters.search ?? ''}
              onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
            />
            <Select
              options={[
                { value: '', label: 'All Roles' },
                { value: 'admin', label: 'Administrator' },
                { value: 'manager', label: 'Manager' },
                { value: 'developer', label: 'Developer' },
                { value: 'designer', label: 'Designer' },
                { value: 'qa', label: 'QA Engineer' },
                { value: 'pm', label: 'Project Manager' },
              ]}
              value={filters.role ?? ''}
              onChange={(e) => setFilters((f) => ({ ...f, role: e.target.value || undefined }))}
              className="w-40"
            />
            <Select
              options={[
                { value: DEFAULT_STATUS_FILTER, label: 'Active & On Leave' },
                { value: 'active', label: 'Active only' },
                { value: 'on_leave', label: 'On Leave only' },
                { value: 'inactive', label: 'Inactive only' },
                { value: '', label: 'All Statuses (incl. Inactive)' },
              ]}
              value={filters.status ?? ''}
              onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value || undefined }))}
              className="w-48"
            />
          </CardBody>
        </Card>

        {/* Employees Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {isLoading ? (
            <p className="text-gray-500 dark:text-gray-400 col-span-full">Loading employees...</p>
          ) : employees.length === 0 ? (
            <div className="col-span-full">
              <Card>
                <EmptyState
                  icon={Users}
                  title="No employees found"
                  description={
                    isAdmin
                      ? 'Add your first team member to get started'
                      : 'No team members match your filters'
                  }
                  action={
                    isAdmin
                      ? {
                          label: 'Add Employee',
                          onClick: () => setIsCreateOpen(true),
                        }
                      : undefined
                  }
                />
              </Card>
            </div>
          ) : (
            employees.map((employee: any) => (
              <Card key={employee.id}>
                <CardBody>
                  <div className="flex items-start gap-4">
                    <Avatar
                      src={employee.avatarUrl}
                      name={`${employee.firstName} ${employee.lastName}`}
                      size="lg"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between">
                        <div>
                          <Link
                            to="/employee/$employeeId"
                            params={{ employeeId: employee.id }}
                            className="font-semibold text-gray-900 dark:text-gray-100 hover:text-primary-600 dark:hover:text-primary-400"
                          >
                            {employee.firstName} {employee.lastName}
                          </Link>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {employee.title ?? employee.email}
                          </p>
                        </div>
                        {isAdmin && (
                          <Dropdown
                            trigger={
                              <button className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded -mt-1 -mr-1">
                                <MoreHorizontal className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                              </button>
                            }
                            align="right"
                            width="w-36"
                          >
                            <DropdownItem onClick={() => setEditingEmployee(employee)}>
                              <div className="flex items-center gap-2">
                                <Pencil className="h-4 w-4" />
                                Edit
                              </div>
                            </DropdownItem>
                            {/* Hide the dead-end: an admin deactivating their own
                                account always fails server-side (CANNOT_DELETE_SELF).
                                Don't offer an action that can't succeed. */}
                            {employee.id !== user?.id && (
                              <DropdownItem
                                onClick={() => openDeactivateDialog(employee)}
                                className="text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950"
                              >
                                <div className="flex items-center gap-2">
                                  <Trash2 className="h-4 w-4" />
                                  Deactivate
                                </div>
                              </DropdownItem>
                            )}
                          </Dropdown>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-2">
                        <Badge variant="primary">
                          {EMPLOYEE_ROLE_LABELS[
                            employee.role as keyof typeof EMPLOYEE_ROLE_LABELS
                          ] ?? employee.role}
                        </Badge>
                        <StatusBadge status={employee.status} />
                      </div>
                    </div>
                  </div>
                  {employee.skills?.length > 0 && (
                    <div className="mt-4 flex flex-wrap gap-1">
                      {employee.skills.slice(0, 4).map((skill: string) => (
                        <Badge key={skill} variant="gray">
                          {skill}
                        </Badge>
                      ))}
                      {employee.skills.length > 4 && (
                        <Badge variant="gray">+{employee.skills.length - 4}</Badge>
                      )}
                    </div>
                  )}
                </CardBody>
              </Card>
            ))
          )}
        </div>
      </PageContainer>

      {/* Create Modal */}
      {isAdmin && (
        <EmployeeFormModal
          isOpen={isCreateOpen}
          onClose={() => setIsCreateOpen(false)}
          employee={null}
        />
      )}

      {/* Edit Modal */}
      {isAdmin && (
        <EmployeeFormModal
          isOpen={!!editingEmployee}
          onClose={() => setEditingEmployee(null)}
          employee={editingEmployee}
        />
      )}

      {/* Deactivate Confirmation */}
      <ConfirmDialog
        isOpen={!!deletingEmployee}
        onClose={closeDeactivateDialog}
        onConfirm={handleDelete}
        title="Deactivate Employee"
        message={`Are you sure you want to deactivate "${deletingEmployee?.firstName} ${deletingEmployee?.lastName}"? They will no longer be able to sign in and all of their active sessions will be revoked. Their historical data (time entries, project and task assignments) is preserved and they can be reactivated later from Edit.`}
        confirmLabel="Deactivate"
        isLoading={deleteEmployee.isPending}
        error={deleteError}
      />
    </>
  );
}
